/**
 * HELIUM Framework - Master Product Page Web Component
 *
 * Owns the parts of the PDP that need to react to a variant change but live
 * outside the <variant-picker> itself: the hidden `<select data-variant-select>`
 * that /cart/add reads, the price display, the gallery, and the URL. The
 * variant-picker block/component isn't nested inside the buy-buttons form
 * (they're sibling blocks), so it can't reach the form on its own - it just
 * works out which variant is selected and dispatches a bubbling
 * `variant:change` event. This component listens for that event and applies
 * the result to the rest of the page.
 */
class ProductPage extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.priceBlock = this.querySelector('[data-product-price-block]');
    this.priceElement = this.querySelector('[data-product-price]');
    this.compareAtElement = this.querySelector('[data-product-compare-at-price]');
    this.unitPriceElement = this.querySelector('[data-product-unit-price]');
    this.gallery = this.querySelector('.product-gallery');
    this.variantPicker = this.querySelector('variant-picker');
    this.hiddenSelect = this.form?.querySelector('[data-variant-select]');
    this.addToCartButton = this.querySelector('[data-add-to-cart-submit]');
    this.quantityInput = this.querySelector('[data-quantity-input]');
    this.purchaseTypes = this.querySelector('purchase-types');
    this.purchaseTypePrompt = this.querySelector('[data-purchase-type-prompt]');
    this.sellingPlanInput = this.form?.querySelector('[data-selling-plan-input]');
    // Only on products tagged as needing a note - see product-note.liquid.
    this.productNote = this.form?.querySelector('[data-product-note]');
    this.productNoteHint = this.querySelector('[data-product-note-hint]');
    // Only present when the product has real Shopify selling plans - see
    // subscription-frequency.liquid. A demo-mode placeholder select has no
    // real plan ids behind it, so it's never wired up here.
    this.sellingPlanSelect = this.querySelector('[data-selling-plan-select]');
    // shop.money_format can contain HTML - currency apps often set it to
    // "<span class=money>£{{amount}}</span>" - and prices here are written as
    // plain text (textContent, or an <input>'s value), so the tags would show
    // literally. Parsing it as HTML and keeping only the text drops the tags
    // and decodes entities such as &pound;, leaving the {{amount}} placeholder.
    this.moneyFormat =
      new DOMParser().parseFromString(this.dataset.moneyFormat || '${{amount}}', 'text/html').body.textContent ||
      '${{amount}}';
    this.freeLabel = this.dataset.freeLabel || 'Free';

    const pricesScript = this.querySelector('[data-product-prices]');
    this.prices = pricesScript ? JSON.parse(pricesScript.textContent) : {};
    this.currentVariantId = this.hiddenSelect ? this.hiddenSelect.value : Object.keys(this.prices)[0];
    // null (rather than defaulting to 'one_time') when a <purchase-types>
    // selector is present but nothing is active yet - e.g. its default is
    // "none", forcing the customer to choose before they can add to cart.
    // Without a selector on the page at all, purchase type doesn't gate
    // anything, so it's treated as always "selected".
    this.purchaseType = this.purchaseTypes?.querySelector('.is-active')?.dataset.purchaseTypeOption || null;
    // The add-to-cart button's disabled attribute is rendered server-side
    // from the variant's own availability - read it once up front so
    // purchase-type gating (below) can be combined with it without
    // clobbering a genuinely sold-out variant.
    this.variantAvailable = this.addToCartButton ? !this.addToCartButton.disabled : true;

    if (this.variantPicker) {
      this.variantPicker.addEventListener('variant:change', (event) => {
        this.handleVariantChange(event.detail.variant);
      });
    }

    if (this.quantityInput) {
      this.quantityInput.addEventListener('change', () => this.updatePrice());
    }

    if (this.productNote) {
      this.productNote.addEventListener('input', () => this.refreshAddToCartAvailability());
    }

    if (this.purchaseTypes) {
      this.purchaseTypes.addEventListener('purchase-type:change', (event) => {
        this.purchaseType = event.detail.value;
        this.clearPurchaseTypePrompt();
        this.refreshAddToCartAvailability();
        this.updateSellingPlan();
        this.updatePrice();
      });
    }

    if (this.sellingPlanSelect) {
      this.sellingPlanSelect.addEventListener('change', () => this.updateSellingPlan());
    }

    // The add-to-cart button stays natively clickable even when a purchase
    // type is required but not chosen yet (see refreshAddToCartAvailability)
    // so this submit listener - rather than a disabled attribute - is what
    // actually blocks the add and prompts the customer. Listening on the
    // form (not just the button's click) also catches an Enter-key submit.
    if (this.form) {
      this.form.addEventListener('submit', (event) => {
        // The button is disabled while the note is empty, but an Enter-key
        // submit from another field can still get here.
        if (!this.hasProductNote()) {
          event.preventDefault();
          event.stopPropagation();
          this.productNote.focus();
          return;
        }

        if (this.purchaseTypes && !this.purchaseType) {
          event.preventDefault();
          event.stopPropagation();
          this.promptPurchaseTypeSelection();
          return;
        }

        // Belt-and-braces resync right before the browser reads the form's
        // fields for submission (cart-drawer.js's document-level listener
        // builds its FormData from this same form once this handler
        // returns) - guards against the selling-plan input ever going stale
        // relative to the select's true value, e.g. a bfcache-restored page
        // where the frequency <select> can come back with a different value
        // than what was last copied into the hidden input, with no 'change'
        // event to catch it.
        this.updateSellingPlan();
      });
    }

    this.refreshAddToCartAvailability();
    this.updateSellingPlan();
  }

  /**
   * Applies a newly-selected variant (reported by <variant-picker>) to the
   * rest of the page.
   * @param {Object} variant
   */
  handleVariantChange(variant) {
    if (!variant) return;

    this.currentVariantId = variant.id;

    if (this.hiddenSelect) {
      this.hiddenSelect.value = variant.id;
      this.hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.updateMedia(variant);
    this.updateURL(variant);
    this.updateAvailability(variant);
    this.updatePrice();
    this.updateStockCounter(variant);
  }

  /**
   * Refreshes the optional stock counter block (blocks/_stock-counter.liquid)
   * for the newly selected variant. Its per-variant stock data and translated
   * labels are all rendered into the block, so this needs no request. Must
   * match the Liquid's state rules.
   * @param {Object} variant
   */
  updateStockCounter(variant) {
    const counter = this.querySelector('[data-stock-counter]');
    if (!counter) return;
    const text = counter.querySelector('[data-stock-counter-text]');
    const dataScript = counter.querySelector('[data-stock-counter-variants]');
    if (!text || !dataScript) return;

    if (!this.stockData) {
      try {
        this.stockData = JSON.parse(dataScript.textContent);
      } catch (error) {
        return;
      }
    }
    const stock = this.stockData[variant.id];
    if (!stock) return;

    const { labelInStock, labelInStockCount, labelLowStock, labelLowStockCount, labelBackorder, labelOutOfStock } =
      counter.dataset;
    const showCount = counter.dataset.showCount === 'true';
    const threshold = Number(counter.dataset.lowStockThreshold) || 0;

    let state = 'in_stock';
    if (!stock.available) state = 'out_of_stock';
    else if (!stock.tracked) state = 'untracked';
    else if (stock.quantity <= 0) state = 'backorder';
    else if (stock.quantity <= threshold) state = 'low_stock';

    const withCount = (label) => label.replace('[count]', stock.quantity);
    const labels = {
      out_of_stock: labelOutOfStock,
      backorder: labelBackorder,
      low_stock: showCount ? withCount(labelLowStockCount) : labelLowStock,
      in_stock: showCount ? withCount(labelInStockCount) : labelInStock,
      untracked: labelInStock,
    };

    counter.className = counter.className.replace(/\bstock-counter--\w+/g, '').trim();
    counter.classList.add(`stock-counter--${state}`);
    counter.hidden = state === 'out_of_stock' && counter.dataset.hideOutOfStock === 'true';
    text.textContent = labels[state] || '';
  }

  /**
   * Gets the current quantity from the quantity selector, if present.
   * @returns {number}
   */
  getQuantity() {
    const value = this.quantityInput ? Number(this.quantityInput.value) : 1;
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  /**
   * The subscription discount percentage (0-100) if the subscription
   * purchase type is currently selected, otherwise 0.
   * @returns {number}
   */
  getSubscriptionDiscount() {
    if (this.purchaseType !== 'subscription') return 0;
    return Number(this.purchaseTypes?.dataset.subscriptionDiscount) || 0;
  }

  /**
   * Updates the displayed price (and compare-at price) for the current
   * variant, quantity, and purchase type, then keeps the add-to-cart
   * button's price-in-label in sync with the same numbers.
   */
  updatePrice() {
    const priceData = this.prices[this.currentVariantId];
    if (!priceData) return;

    const quantity = this.getQuantity();
    const discount = this.getSubscriptionDiscount();

    let unitCents = priceData.cents;
    let compareUnitCents = priceData.compareAtCents || null;

    if (discount > 0) {
      compareUnitCents = priceData.cents;
      unitCents = Math.round((priceData.cents * (100 - discount)) / 100);
    }

    if (this.priceElement) {
      const totalCents = unitCents * quantity;
      this.priceElement.textContent = totalCents === 0 ? this.freeLabel : this.formatMoney(totalCents);
      this.priceElement.classList.toggle('price-free', totalCents === 0);
    }

    if (this.compareAtElement) {
      this.compareAtElement.hidden = !compareUnitCents;
      if (compareUnitCents) {
        this.compareAtElement.textContent = this.formatMoney(compareUnitCents * quantity);
      }
    }

    if (this.priceBlock) {
      this.priceBlock.classList.toggle('product-price--sale', Boolean(compareUnitCents));
    }

    // Price per unit (e.g. "£1.20/100g") - pre-formatted by Liquid, per variant.
    if (this.unitPriceElement) {
      this.unitPriceElement.hidden = !priceData.unitPrice;
      this.unitPriceElement.textContent = priceData.unitPrice || '';
    }

    this.updateAddToCartPrice(unitCents);
  }

  /**
   * Keeps the add-to-cart button's optional price-in-label in sync with the
   * current unit price (post subscription-discount) and quantity.
   * @param {number} unitCents
   */
  updateAddToCartPrice(unitCents) {
    if (!this.addToCartButton || this.addToCartButton.disabled) return;
    if (this.addToCartButton.dataset.priceInButton !== 'true') return;

    const quantity = this.getQuantity();
    const totalCents = unitCents * quantity;
    const priceText = totalCents === 0 ? this.freeLabel : this.formatMoney(totalCents);
    this.addToCartButton.value = `${this.addToCartButton.dataset.labelBase} | ${priceText}`;
  }

  /**
   * Formats an amount in cents using the shop's money format
   * (`{{ shop.money_format }}`, e.g. "£{{amount}}").
   * @param {number} cents
   * @returns {string}
   */
  formatMoney(cents) {
    const placeholder = this.moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
    const kind = placeholder ? placeholder[1] : 'amount';

    let precision = 2;
    let thousands = ',';
    let decimal = '.';

    if (kind === 'amount_no_decimals') precision = 0;
    if (kind === 'amount_with_comma_separator') {
      thousands = '.';
      decimal = ',';
    }
    if (kind === 'amount_no_decimals_with_comma_separator') {
      precision = 0;
      thousands = '.';
      decimal = ',';
    }

    const [whole, fraction] = (cents / 100).toFixed(precision).split('.');
    const groupedWhole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const value = fraction ? `${groupedWhole}${decimal}${fraction}` : groupedWhole;

    return placeholder ? this.moneyFormat.replace(placeholder[0], value) : value;
  }

  /**
   * Moves the gallery to the selected variant's featured image - scrolls the
   * tiles grid into view when present, and (since a tiles gallery still
   * falls back to a swiper on mobile, and a 'swiper' gallery style uses one
   * on every breakpoint) also slides the swiper to the matching slide when
   * one is initialized. Swiper attaches itself as `.swiper` on its container
   * element, so no import is needed here to read the live instance.
   * @param {Object} variant
   */
  updateMedia(variant) {
    if (!this.gallery) return;
    if (this.gallery.dataset.mediaSource !== 'variant') return;
    if (this.gallery.dataset.singleOption !== 'true') return;
    // Gallery items carry the product *media* id (the gallery loops
    // product.media), which is what variant.featured_media.id refers to.
    if (!variant.featured_media) return;

    const mediaId = String(variant.featured_media.id);

    this.gallery.querySelectorAll('[data-media-id]').forEach((el) => el.classList.remove('is-active'));

    const tileTarget = this.gallery.querySelector(`.product-gallery__tiles [data-media-id="${mediaId}"]`);
    if (tileTarget) {
      tileTarget.classList.add('is-active');
      tileTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }

    const swiperEl = this.gallery.querySelector('.product-gallery__swiper');
    if (swiperEl) {
      const slides = Array.from(swiperEl.querySelectorAll('[data-media-id]'));
      const slideIndex = slides.findIndex((el) => el.dataset.mediaId === mediaId);
      if (slideIndex > -1) {
        slides[slideIndex].classList.add('is-active');
        if (swiperEl.swiper) swiperEl.swiper.slideTo(slideIndex);
      }
    }
  }

  /**
   * Keeps browser URL updated with ?variant=12345678 without refreshing the page
   * @param {Object} variant
   */
  updateURL(variant) {
    // A <product-page> rendered inside the quick-add modal is a preview of
    // a possibly different product than the one the customer is actually
    // on - it must never rewrite the real page's address bar.
    if (this.closest('quick-add-modal')) return;
    window.history.replaceState({}, '', `?variant=${variant.id}`);
  }

  /**
   * Records the selected variant's availability and refreshes the
   * add-to-cart button accordingly. The price segment of the label (when
   * enabled) is applied separately by updatePrice()/updateAddToCartPrice()
   * so it stays in sync with quantity and purchase-type changes too.
   * @param {Object} variant
   */
  updateAvailability(variant) {
    this.variantAvailable = Boolean(variant.available);
    this.refreshAddToCartAvailability();
  }

  /**
   * Disables add-to-cart when the selected variant is unavailable. When a
   * <purchase-types> selector is present but the customer hasn't picked
   * one-time/subscription yet (its default is "none", so nothing is active
   * until they choose), the button is left natively enabled - just dimmed
   * via `is-awaiting-selection` - because a genuinely `disabled` button
   * can't be clicked at all, and the whole point is that clicking it (or
   * pressing Enter) is what triggers the prompt in promptPurchaseTypeSelection()
   * (see the form 'submit' listener in connectedCallback). Sold-out takes
   * priority over the purchase-type prompt since it's the more specific,
   * unrecoverable-by-the-customer reason.
   */
  refreshAddToCartAvailability() {
    if (!this.addToCartButton) return;

    if (!this.variantAvailable) {
      this.addToCartButton.disabled = true;
      this.addToCartButton.classList.remove('is-awaiting-selection');
      this.addToCartButton.value = this.addToCartButton.dataset.labelSoldOut;
      return;
    }

    // A required note (product-note.liquid) keeps the button genuinely
    // disabled until it's filled in - the hint under the note explains why.
    const hasNote = this.hasProductNote();
    if (this.productNoteHint) this.productNoteHint.hidden = hasNote;

    this.addToCartButton.disabled = !hasNote;
    const purchaseTypeChosen = !this.purchaseTypes || Boolean(this.purchaseType);
    this.addToCartButton.classList.toggle('is-awaiting-selection', !purchaseTypeChosen);

    if (this.addToCartButton.dataset.priceInButton !== 'true') {
      this.addToCartButton.value = this.addToCartButton.dataset.labelBase;
    }
  }

  /**
   * Whether the required note is filled in (whitespace doesn't count).
   * Always true for products that don't need one.
   * @returns {boolean}
   */
  hasProductNote() {
    return !this.productNote || this.productNote.value.trim() !== '';
  }

  /**
   * Highlights the purchase-type selector and shows an explanatory message
   * when the customer tries to add to cart without choosing one - called
   * from the form's 'submit' listener. Re-triggers the CSS shake animation
   * on repeated attempts by forcing a reflow between removing and re-adding
   * the class, since re-adding an already-present class alone wouldn't
   * restart it.
   */
  promptPurchaseTypeSelection() {
    if (!this.purchaseTypes) return;

    this.purchaseTypes.classList.remove('is-invalid');
    void this.purchaseTypes.offsetWidth;
    this.purchaseTypes.classList.add('is-invalid');

    if (this.purchaseTypePrompt) this.purchaseTypePrompt.hidden = false;

    this.purchaseTypes.scrollIntoView({ behavior: 'smooth', block: 'center' });
    this.purchaseTypes.querySelector('input[data-purchase-type-option], button[data-purchase-type-option]')?.focus();
  }

  /**
   * Clears the highlight/message from promptPurchaseTypeSelection() once
   * the customer actually picks a purchase type.
   */
  clearPurchaseTypePrompt() {
    this.purchaseTypes?.classList.remove('is-invalid');
    if (this.purchaseTypePrompt) this.purchaseTypePrompt.hidden = true;
  }

  /**
   * Keeps the add-to-cart form's hidden selling-plan field in sync with the
   * real selling-plan select (only rendered when the product has actual
   * Shopify selling plans - see subscription-frequency.liquid). Giving the
   * hidden input a `name` is what makes /cart/add treat this as a
   * subscription order; it's left unnamed - and so left out of the
   * submitted form entirely - for a one-time purchase, or for a demo-only
   * frequency select with no real plan id behind it.
   */
  updateSellingPlan() {
    if (!this.sellingPlanInput) return;

    if (this.purchaseType === 'subscription' && this.sellingPlanSelect) {
      this.sellingPlanInput.name = 'selling_plan';
      this.sellingPlanInput.value = this.sellingPlanSelect.value;
    } else {
      this.sellingPlanInput.removeAttribute('name');
      this.sellingPlanInput.value = '';
    }
  }

  /**
   * The selling plan id that should be submitted for the current
   * purchase-type/frequency choice, or '' for a one-time purchase - computed
   * straight from the live select rather than read back off
   * `sellingPlanInput`. Third-party subscription app embeds (e.g. Seal
   * Subscriptions) commonly watch the cart form for a `selling_plan` field
   * and take over managing it, which can stomp the value our own
   * updateSellingPlan() just wrote before the request actually goes out.
   * cart-drawer.js calls this to force the correct id into the outgoing
   * request body directly, rather than trusting whatever's currently sitting
   * in the DOM input at submit time.
   * @returns {string}
   */
  getSellingPlanId() {
    if (this.purchaseType !== 'subscription' || !this.sellingPlanSelect) return '';
    return this.sellingPlanSelect.value;
  }
}

// Guarded because this file is loaded both by the product section itself and
// globally by the quick-add modal (so <product-page> is registered before a
// customer ever opens it from a non-product page) - on an actual product
// page both copies load, and re-registering the same tag name throws.
if (!customElements.get('product-page')) {
  customElements.define('product-page', ProductPage);
}
